(function () {
  'use strict';

  var indexPageSize = 250;
  var cacheLifetime = 60000;
  var cacheLimit = 6;
  var storageKey = 'theme:collection-stock:v2';
  var indexCache = new Map();
  var storageLoaded = false;
  var pending = new WeakMap();

  function changed(message) {
    var error = new Error(message);
    error.name = 'CollectionChangedError';
    return error;
  }

  function validProducts(products, count) {
    return Array.isArray(products) && products.length === count &&
      products.every(function (product) {
        return product && typeof product.id === 'string' && typeof product.available === 'boolean';
      }) && new Set(products.map(function (product) { return product.id; })).size === count;
  }

  function cacheKey(base, grid) {
    var url = new URL(base);
    ['page', 'view', 'phcursor', 'section_id', 'sections'].forEach(function (param) {
      url.searchParams.delete(param);
    });
    if (!url.searchParams.get('sort_by')) url.searchParams.set('sort_by', grid.dataset.stockDefaultSort);
    url.searchParams.sort();
    url.hash = '';
    return url.href + '|' + grid.dataset.stockContext;
  }

  function fresh(entry, count) {
    var age = Date.now() - entry.time;
    return entry.count === count && age >= 0 && age < cacheLifetime;
  }

  function readCache(key, count) {
    if (!storageLoaded) {
      storageLoaded = true;
      try {
        var entries = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
        if (Array.isArray(entries)) entries.slice(-cacheLimit).forEach(function (entry) {
          if (entry && typeof entry.key === 'string' && fresh(entry, entry.count) &&
              validProducts(entry.products, entry.count)) indexCache.set(entry.key, entry);
        });
      } catch (error) { /* Storage may be disabled; memory caching still works. */ }
    }
    var entry = indexCache.get(key);
    if (entry && fresh(entry, count)) return entry.products;
    indexCache.delete(key);
    return null;
  }

  function saveCache() {
    indexCache.forEach(function (entry, key) {
      if (!fresh(entry, entry.count)) indexCache.delete(key);
    });
    while (indexCache.size > cacheLimit) indexCache.delete(indexCache.keys().next().value);
    try { sessionStorage.setItem(storageKey, JSON.stringify(Array.from(indexCache.values()))); }
    catch (error) { /* Do not fail the grid when storage is full or unavailable. */ }
  }

  function storeIndex(key, count, products) {
    indexCache.delete(key);
    indexCache.set(key, { key: key, count: count, time: Date.now(), products: products });
    saveCache();
  }

  function discardIndex(key) {
    indexCache.delete(key);
    saveCache();
  }

  function currentCards(grid) {
    return Array.from(grid.children).filter(function (node) { return node.matches('[data-product-handle]'); });
  }

  function matchesNativePage(products, grid) {
    var page = Number(grid.dataset.stockPage);
    var size = Number(grid.dataset.stockPageSize);
    var expected = products.slice((page - 1) * size, page * size);
    var cards = currentCards(grid);
    return cards.length === expected.length && cards.every(function (card, index) {
      return card.dataset.productId === expected[index].id &&
        !card.querySelector('.grid-product__tag--sold-out') === expected[index].available;
    });
  }

  // Keep Shopify's selected order within each stock group, then paginate.
  function stockFirst(products) {
    return products.filter(function (product) { return product.available; })
      .concat(products.filter(function (product) { return !product.available; }));
  }

  async function mapPages(pages, callback) {
    var results = new Array(pages.length);
    var next = 0;
    await Promise.all(Array.from({ length: Math.min(4, pages.length) }, async function () {
      while (next < pages.length) {
        var index = next++;
        results[index] = await callback(pages[index]);
      }
    }));
    return results;
  }

  function viewUrl(base, view, page) {
    var url = new URL(base);
    url.searchParams.set('view', view);
    url.searchParams.set('page', page);
    url.searchParams.delete('phcursor');
    return url;
  }

  async function request(url, format, job) {
    var response = await fetch(url, { signal: job.controller.signal });
    if (!response.ok) throw new Error('Collection request failed: ' + response.status);
    return await response[format]();
  }

  async function loadIndex(base, expectedCount, job) {
    // The page already supplies the count, so all index pages can start together.
    var pageCount = Math.ceil(expectedCount / indexPageSize);
    var pages = Array.from({ length: pageCount }, function (_, index) { return index + 1; });
    var results = await mapPages(pages, async function (page) {
      var result = await request(viewUrl(base, 'stock-index', page), 'json', job);
      if (result.count !== expectedCount || result.page !== page || result.pages !== pageCount ||
          !Array.isArray(result.products) ||
          result.products.length !== Math.min(indexPageSize, expectedCount - (page - 1) * indexPageSize)) {
        throw changed('Collection changed while loading');
      }
      return result;
    });
    var products = results.reduce(function (all, result) { return all.concat(result.products); }, []);
    if (!validProducts(products, expectedCount)) {
      throw changed('Incomplete collection stock index');
    }
    return products;
  }

  function cardPlan(missing, products) {
    var positions = new Map(products.map(function (product, index) { return [product.id, index]; }));

    function chunks(items, size) {
      var pages = new Map();
      items.forEach(function (product) {
        var page = Math.floor(positions.get(product.id) / size) + 1;
        if (!pages.has(page)) pages.set(page, []);
        pages.get(page).push(product);
      });
      return Array.from(pages, function (entry) {
        var page = entry[0];
        var items = entry[1];
        var states = new Set(items.map(function (product) { return product.available; }));
        var mode = size === 1 || states.size === 2 ? null : items[0].available;
        var expected = products.slice((page - 1) * size, page * size);
        if (mode !== null) expected = expected.filter(function (product) { return product.available === mode; });
        var view = size === 1 ? 'stock-product' : size === 20 ? 'stock-compact' : 'stock-grid';
        if (size !== 1 && mode !== null) view += mode ? '-available' : '-sold-out';
        return { page: page, view: view, expected: expected, items: items };
      });
    }

    function cost(plan) {
      // Prefer fewer request waves, then fewer rendered cards within that budget.
      return Math.ceil(plan.length / 4) * 60 + plan.length * 3 +
        plan.reduce(function (total, chunk) { return total + chunk.expected.length; }, 0);
    }

    if (missing.length <= 4) return chunks(missing, 1);
    var wide = chunks(missing, 100);
    var plan = wide.slice();
    wide.forEach(function (chunk) {
      var alternatives = [chunks(chunk.items, 20)];
      if (chunk.items.length <= 4) alternatives.push(chunks(chunk.items, 1));
      var position = plan.indexOf(chunk);
      var best = plan;
      alternatives.forEach(function (smaller) {
        var candidate = plan.slice(0, position).concat(smaller, plan.slice(position + 1));
        if (cost(candidate) < cost(best)) best = candidate;
      });
      plan = best;
    });
    return plan;
  }

  async function pageContent(base, products, selected, expectedCount, grid, job) {
    var existing = new Map();
    var modals = new Map();
    Array.from(grid.children).forEach(function (node) {
      if (node.matches('[data-product-handle]')) {
        existing.set(node.dataset.productId, node);
      } else if (node.matches('.modal--quick-shop')) {
        modals.set(node.dataset.productId, node);
      }
    });
    var missing = selected.filter(function (product) {
      var card = existing.get(product.id);
      return !card || (!card.querySelector('.grid-product__tag--sold-out')) !== product.available;
    });
    // Reuse current cards; choose 1, 20, or 100-product batches for missing cards.
    var chunks = await mapPages(cardPlan(missing, products), async function (batch) {
      var html = await request(viewUrl(base, batch.view, batch.page), 'text', job);
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var chunk = doc.querySelector('[data-collection-stock-page]');
      if (!chunk || Number(chunk.dataset.stockCount) !== expectedCount ||
          Number(chunk.dataset.collectionStockPage) !== batch.page) {
        throw changed('Collection changed while rendering');
      }
      var templates = Array.from(chunk.querySelectorAll('template[data-stock-product-id]'));
      if (templates.length !== batch.expected.length || templates.some(function (template, index) {
        return template.dataset.stockProductId !== batch.expected[index].id;
      })) {
        throw changed('Collection order changed while rendering');
      }
      return templates;
    });
    var cards = new Map();
    chunks.forEach(function (chunk) {
      chunk.forEach(function (template) { cards.set(template.dataset.stockProductId, template); });
    });
    // Validate every card before moving existing DOM nodes out of the live grid.
    return selected.map(function (product) {
      var template = cards.get(product.id);
      var card = existing.get(product.id);
      if (template && (template.dataset.stockAvailable === 'true') !== product.available) {
        throw changed('Stock changed while rendering');
      }
      if (card && (!card.querySelector('.grid-product__tag--sold-out')) === product.available) {
        return modals.has(product.id) ? [card, modals.get(product.id)] : [card];
      }
      if (!template) {
        throw changed('Stock changed while rendering');
      }
      return Array.from(template.content.childNodes);
    });
  }

  async function sort(container, grid, job) {
    var count = Number(grid.dataset.stockCount);
    var pageSize = Number(grid.dataset.stockPageSize);
    var page = Number(grid.dataset.stockPage);
    var base = location.href;
    var key = cacheKey(base, grid);
    var timeout = setTimeout(function () { job.controller.abort(); }, 8000);
    grid.setAttribute('aria-busy', 'true');
    grid.dataset.stockSortState = 'loading';
    try {
      // Retry once if inventory or product ordering changes between requests.
      for (var attempt = 0; attempt < 2; attempt++) {
        try {
          var products;
          var cards = currentCards(grid);
          if (page === 1 && cards.length === count) {
            // The whole collection is already on this page; no requests needed.
            products = cards.map(function (card) {
              return { id: card.dataset.productId, available: !card.querySelector('.grid-product__tag--sold-out') };
            });
          } else {
            products = attempt === 0 ? readCache(key, count) : null;
            if (products && !matchesNativePage(products, grid)) {
              discardIndex(key);
              products = null;
            }
            if (!products) {
              products = await loadIndex(base, count, job);
              if (job.cancelled || job.controller.signal.aborted || !container.isConnected) return false;
              if (!matchesNativePage(products, grid)) throw changed('Collection changed since the page was rendered');
              storeIndex(key, count, products);
            }
          }
          var selected = stockFirst(products).slice((page - 1) * pageSize, page * pageSize);
          var unchanged = cards.length === selected.length && cards.every(function (card, index) {
            return card.dataset.productId === selected[index].id &&
              !card.querySelector('.grid-product__tag--sold-out') === selected[index].available;
          });
          if (!unchanged) {
            var content = await pageContent(base, products, selected, count, grid, job);
            if (job.cancelled || job.controller.signal.aborted || !container.isConnected) return false;
            var fragment = document.createDocumentFragment();
            content.forEach(function (nodes) {
              nodes.forEach(function (node) { fragment.appendChild(node); });
            });
            grid.replaceChildren(fragment);
          }
          grid.dataset.stockSortState = 'ready';
          return true;
        } catch (error) {
          if (error.name === 'CollectionChangedError') discardIndex(key);
          if (error.name !== 'CollectionChangedError' || attempt === 1 || job.controller.signal.aborted) throw error;
        }
      }
    } catch (error) {
      if (job.cancelled || !container.isConnected) {
        grid.dataset.stockSortState = 'cancelled';
        return false;
      }
      // Keep the server-rendered grid usable if a request fails.
      grid.dataset.stockSortState = 'fallback';
      console.warn('Could not apply collection stock order.', error);
      return true;
    } finally {
      clearTimeout(timeout);
      job.controller.abort();
      pending.delete(grid);
      grid.removeAttribute('aria-busy');
    }
  }

  window.theme = window.theme || {};
  theme.CollectionStockSort = {
    load: function (container) {
      var grid = container.querySelector('[data-stock-sort]');
      if (!grid) return Promise.resolve();
      if (Number(grid.dataset.stockCount) === 0 || grid.dataset.stockSortState === 'ready') {
        grid.dataset.stockSortState = 'ready';
        return Promise.resolve();
      }
      if (!pending.has(grid)) {
        var job = { controller: new AbortController(), cancelled: false };
        pending.set(grid, job);
        job.promise = sort(container, grid, job);
        return job.promise;
      }
      return pending.get(grid).promise;
    },
    cancel: function (container) {
      var grid = container && container.querySelector('[data-stock-sort]');
      var job = grid && pending.get(grid);
      if (job) {
        job.cancelled = true;
        job.controller.abort();
      }
    }
  };
})();
